/**
 * @file searchMatch.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-09
 * @modified 2026-09-29
 * @description Shared free-text search matching — the browser's half of the
 *              one search rule every screen follows (the server's half is
 *              utils/searchTerms.js at the project root; the "?" beside SEARCH,
 *              components/SearchHelp.js, explains it to the user):
 *
 *              - every word must appear somewhere in the row, in any order;
 *              - part-words count, capitals do not matter;
 *              - "double quotes" (or curly ones) keep a phrase together;
 *              - with `amounts`, 1234.50 / 1,234.50 / £1,234.50 match, and a
 *                whole number matches the pounds (335 → £335.00–£335.99).
 */

/**
 * Parse a search string into lower-cased terms. Whitespace separates terms;
 * text inside double quotes is kept together as one term.
 *   parseSearchTerms('red "farm feed" 22')  → ['red', 'farm feed', '22']
 * @param {string} query
 * @returns {string[]}
 */
export function parseSearchTerms(query) {
  if (!query) return [];
  const text = String(query).replace(/[“”„]/g, '"');
  const terms = [];
  // "quoted phrase" | bare-token
  const re = /"([^"]*)"|(\S+)/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const raw = m[1] !== undefined ? m[1] : m[2];
    const term = (raw || '').replace(/"/g, '').trim().toLowerCase();
    if (term && !terms.includes(term)) terms.push(term);
  }
  return terms;
}

const AMOUNT_RE = /^[£$€]?-?(\d{1,3}(,\d{3})+|\d+)(\.\d{1,2})?$/;

/** Does `term` name one of `amounts`? Same rule as the server's buildWordSearch. */
function matchesAmount(term, amounts) {
  if (!amounts || !amounts.length || !AMOUNT_RE.test(term)) return false;
  const n = term.replace(/[£$€,]/g, '');
  const prefix = n.includes('.') ? n : `${n}.`;
  return amounts.some(a => {
    const v = Number(a);
    return Number.isFinite(v) && v.toFixed(2).startsWith(prefix);
  });
}

/**
 * True when every term in `query` is found within `fields` (joined + lowered).
 * Empty / whitespace query matches everything. Falsy field values are ignored.
 * @param {Array<string|null|undefined>} fields  values to search within
 * @param {string} query
 * @param {object} [opts]
 * @param {Array<number|string>} [opts.amounts]  the row's amounts, if it shows any
 * @returns {boolean}
 */
export function matchesAllTerms(fields, query, { amounts = null } = {}) {
  const terms = parseSearchTerms(query);
  if (terms.length === 0) return true;
  const haystack = (Array.isArray(fields) ? fields : [fields])
    .filter(v => v !== null && v !== undefined && v !== '')
    .map(v => String(v).toLowerCase())
    .join(' ');
  return terms.every(t => haystack.includes(t) || matchesAmount(t, amounts));
}

/**
 * Group id → every searchable value carried by ANY member of that group.
 *
 * Rows that belong to a contract group are filtered one at a time but shown as
 * one row, and that row displays the whole group's detail — the blends table
 * prints every member's supplier contract number on the group header.
 * Matching each member against the search on its own therefore hides
 * members the header has just advertised: search a supplier contract number
 * held by one product of a three-product contract and the group breaks apart
 * into a single loose row, with no sign the other two exist. On the live blends
 * data a search for "PR-" splits 9 of the 29 multi-product groups that way.
 *
 * Pooling the group's values means the group matches on anything any member
 * carries and stays whole — the same whole-group rule the CONTRACTED toggle
 * already applies.
 *
 * @param {Array<object>} rows    every row in play, not just the current page
 * @param {Array<{get: Function}>} fields  the active search fields
 * @param {string} groupKey       the grouping column
 * @returns {Map<any, Array>}     group id → pooled field values
 */
export function buildGroupHaystacks(rows, fields, groupKey = 'contract_group_id') {
  const map = new Map();
  for (const row of rows || []) {
    const groupId = row?.[groupKey];
    if (!groupId) continue;
    let pooled = map.get(groupId);
    if (!pooled) { pooled = []; map.set(groupId, pooled); }
    for (const field of fields) pooled.push(field.get(row));
  }
  return map;
}

/**
 * Trigger-label formatter for a "which fields to search" MultiSelectDropdown.
 * Pass straight to MultiSelectDropdown's `summarize` prop.
 *   all selected            → "Search All"
 *   exactly one selected    → that option's label
 *   several (but not all)    → "N Selected"
 *   none selected            → "None Selected"
 */
export function searchFieldsSummary(value, options) {
  const total = options.length;
  const n = value.length;
  if (total > 0 && n === total) return 'Search All';
  if (n === 1) {
    const opt = options.find(o => o.value === value[0]);
    return opt?.label || value[0];
  }
  if (n === 0) return 'None Selected';
  return `${n} Selected`;
}
